import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FaUsers, FaUtensils, FaSmile, FaAward, FaCalendarCheck } from 'react-icons/fa';
import './Stats.css';

const ICON_MAP = {
  chef: <FaUtensils />, smile: <FaSmile />, calendar: <FaCalendarCheck />,
  award: <FaAward />, users: <FaUsers />
};

function Counter({ value, suffix, start }) {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!start) return;
    const step = Math.max(1, Math.floor(value / 60));
    let cur = 0;
    const t = setInterval(() => {
      cur += step;
      if (cur >= value) { setN(value); clearInterval(t); }
      else setN(cur);
    }, 25);
    return () => clearInterval(t);
  }, [value, start]);

  return <span>{n}{suffix}</span>;
}

function Stats() {
  const [stats, setStats] = useState([]);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  /* ---------- Fetch stats ---------- */
  useEffect(() => {
    axios.get('https://api.hospifylink.com/api/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  /* ---------- Detect when the section enters the viewport ---------- */
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    // Fallback: if the browser doesn't support IntersectionObserver,
    // show everything immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);  // fire only once
          }
        });
      },
      {
        threshold: 0.15,          // trigger when ~15% of the section is visible
        rootMargin: '0px 0px -60px 0px'  // small offset so it fires just before it fully enters
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /* Cap columns at 6 max, min 1 */
  const cols = Math.min(stats.length || 1, 6);

  return (
    <section className="stats-root" ref={sectionRef}>
      <div
        className={`stats-container ${inView ? 'stats-in-view' : ''}`}
        style={{ '--stats-cols': cols }}
      >
        {stats.map((s, i) => {
          /* Alternate direction: even index → from left, odd index → from right */
          const direction = i % 2 === 0 ? 'from-left' : 'from-right';

          return (
            <div
              key={s.id || i}
              className={`stats-card stats-card-${direction}`}
              style={{ animationDelay: `${i * 0.12}s` }}
            >
              <div className="stats-icon">{ICON_MAP[s.icon] || <FaUtensils />}</div>
              <h3 className="stats-value">
                <Counter value={s.value} suffix={s.suffix} start={inView} />
              </h3>
              <p className="stats-label">{s.label}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default Stats;
